/**
 * Dónde cae una estancia nueva en el calendario de una habitación, comparada con la de quien la
 * ocupa ahora. Es lo mismo que decide el servidor al guardarla (`activateLease`), dicho antes de
 * guardar para que nadie se lleve una sorpresa:
 *
 *   · se pisan las fechas → no cabe: hay que cambiar las fechas o la habitación;
 *   · terminó antes de que entrara quien vive ahí → un contrato antiguo: se guarda ya terminado;
 *   · empieza cuando quien vive ahí ya se fue → su contrato se da por terminado;
 *   · empieza cuando salga quien vive ahí → queda programado y se activa solo ese día.
 */
import { formatShortDate } from '../../lib/format';

export interface Ocupante {
  /** `YYYY-MM-DD`. Puede faltar si la API es anterior a este cambio. */
  from?: string | null;
  until: string | null;
  tenantName: string | null;
}

export function colocacion(
  ocupante: Ocupante,
  desde: string,
  hasta: string,
  hoy: string,
): { problema: boolean; texto: string } {
  const quien = ocupante.tenantName ?? 'otra persona';
  const estancia = `${ocupante.from ? `${formatShortDate(ocupante.from)} – ` : 'hasta el '}${
    ocupante.until ? formatShortDate(ocupante.until) : 'sin fecha de salida'
  }`;
  if (!desde) {
    return { problema: false, texto: `Ahora vive aquí ${quien} (${estancia}). Pon las fechas y se coloca según el calendario de la habitación.` };
  }

  const empiezaOcupante = ocupante.from ?? '0000-00-00';
  const terminaOcupante = ocupante.until ?? '9999-12-31';
  const terminaNueva = hasta || '9999-12-31';

  if (terminaNueva < empiezaOcupante) {
    return terminaNueva < hoy
      ? { problema: false, texto: `Es anterior a ${quien}, que entró el ${formatShortDate(empiezaOcupante)}: se guarda como un contrato ya terminado.` }
      : { problema: false, texto: `Termina antes de que entre ${quien} (${formatShortDate(empiezaOcupante)}).` };
  }
  if (desde > terminaOcupante) {
    return terminaOcupante < hoy
      ? { problema: false, texto: `${quien} salió el ${formatShortDate(terminaOcupante)}: su contrato se da por terminado y este queda como el actual.` }
      : {
          problema: false,
          texto: `Entra cuando salga ${quien} (${formatShortDate(terminaOcupante)}): queda programado y se activa solo el ${formatShortDate(desde)}.`,
        };
  }
  return {
    problema: true,
    texto: `Se pisa con la estancia de ${quien} (${estancia}). Cambia las fechas o elige otra habitación.`,
  };
}
